import { useCallback } from "react";
import {
  BankIcon,
  CashSpecialIcon,
  Title,
  WalletIcon,
  AddCardIcon,
} from "lola-framework-ui-test";

import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import {
  usePayoutClaimPricing,
  type PayoutCategory,
} from "../hooks/usePayoutClaimPricing";
import type {
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
} from "../interfaces/payment-method";
import { paymentType } from "../interfaces/payment";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import { PayoutInfo } from "./PayoutInfo";
import {
  formatBankAccountLabel,
  looksLikePayerCode,
} from "../utils/bankAccountLabel";
import { ScrollFadeIndicator } from "./ScrollFadeIndicator";
import { CloseLight } from "../assets/navIcons";

const PICKER_SKELETON_ROWS = 3;

const PickerListSkeleton = () => (
  <>
    {Array.from({ length: PICKER_SKELETON_ROWS }, (_, i) => (
      <div key={i} className="payout-skeleton" aria-hidden="true">
        <span className="payout-skeleton-icon" />
        <span className="payout-skeleton-lines">
          <span className="payout-skeleton-line" />
          <span className="payout-skeleton-line short" />
        </span>
      </div>
    ))}
  </>
);

export type PayoutMethodCategory = PayoutCategory;

export type FlatPayoutPick =
  | {
      kind: "card";
      card: { paymentMethodId: string; brand: string; last4: string };
    }
  | { kind: "bank"; account: IBankAccountPaymentMethod }
  | { kind: "method"; category: Exclude<PayoutCategory, "CARD" | "BANK"> }
  | { kind: "add" }
  | { kind: "addBank" };

export interface SelectPaymentMethodSlotProps {
  selected?: PayoutMethodCategory | null;
  selectedPaymentMethodId?: string | null;
  cards: ICardPaymentMethod[];
  cardsLoading?: boolean;
  accounts: IBankAccountPaymentMethod[];
  accountsLoading?: boolean;
  /** Instruments or pre-quote still unresolved — full-list shimmer. */
  loading?: boolean;
  bankLabels?: Record<string, string>;
  onPick: (pick: FlatPayoutPick) => void;
  onClose: () => void;
}

/**
 * Flat payout picker: saved destination cards and bank accounts + type rows
 * that still need a details popup (cash) or inline commit (other) + add CTAs.
 * No CARD or BANK type rows.
 */
export const SelectPaymentMethodSlot: React.FC<
  SelectPaymentMethodSlotProps
> = ({
  selected = null,
  selectedPaymentMethodId = null,
  cards,
  cardsLoading = false,
  accounts,
  accountsLoading = false,
  loading = false,
  bankLabels = {},
  onPick,
  onClose,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn } = colorConfig;

  const { availableCategories, preQuoteLoading } = usePayoutClaimPricing({
    enableQuote: false,
  });
  const showCash = availableCategories.has("CASH");
  const showCards = availableCategories.has("CARD");
  const showBank = availableCategories.has("BANK");
  const showOther = availableCategories.has("OTHER");
  const hasAny = showCash || showCards || showBank || showOther;
  const listLoading =
    loading ||
    preQuoteLoading ||
    (showCards && cardsLoading && cards.length === 0) ||
    (showBank && accountsLoading && accounts.length === 0);
  const cardSubtitle = t("payment.debitCard");
  const bankSubtitle = t("payment.bankAccount");

  const pickIcon = useCallback((brand: string) => {
    let finalIcon: string | null = null;
    if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
    if (brand.toLocaleUpperCase().includes("MASTERCARD"))
      finalIcon = masterCardLogo;
    return finalIcon ? (
      <img src={finalIcon} alt={`${brand} logo`} />
    ) : (
      <span />
    );
  }, []);

  const bankTitle = (account: IBankAccountPaymentMethod): string => {
    const stored = account.bankName?.trim();
    const mapped = stored ? bankLabels[stored.toUpperCase()] : undefined;
    const name = stored && !looksLikePayerCode(stored) ? stored : mapped;
    return (
      formatBankAccountLabel(name, account.maskedAccountNumber) ??
      name ??
      bankSubtitle
    );
  };

  return (
    <section className="select-payer-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title title={t("payoutClaim.selectPayoutMethod")} align="center" color={bodyCopy} />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <section
        className="select-payer-list select-funding-list"
        aria-busy={listLoading}
        style={
          {
            "--bgIcon": backgroundBtn,
            "--iconOnBrand": textColorBtn,
          } as React.CSSProperties
        }
      >
        <ScrollFadeIndicator color={bodyCopy} />
        {listLoading ? <PickerListSkeleton /> : null}
        {!listLoading && showCards &&
          cards.map((card) => {
            const displayName = `${(card.brand || "").toUpperCase()}-${card.last4}`;
            const isSelected =
              selectedPaymentMethodId === card.paymentMethodId;
            return (
              <PayoutInfo
                key={card.paymentMethodId}
                title={displayName}
                subtitle={cardSubtitle}
                icon={pickIcon(card.brand)}
                className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
                onChange={() =>
                  onPick({
                    kind: "card",
                    card: {
                      paymentMethodId: card.paymentMethodId,
                      brand: card.brand,
                      last4: card.last4,
                    },
                  })
                }
              />
            );
          })}
        {!listLoading && showBank &&
          accounts.map((account) => {
            const isSelected =
              selectedPaymentMethodId === account.paymentMethodId;
            return (
              <PayoutInfo
                key={account.paymentMethodId}
                title={bankTitle(account)}
                subtitle={bankSubtitle}
                icon={<BankIcon size={22} colors={[bodyCopy, bodyCopy]} />}
                className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
                onChange={() => onPick({ kind: "bank", account })}
              />
            );
          })}
        {!listLoading && showCash && (
          <PayoutInfo
            title={t("payment.cashPickup")}
            subtitle=""
            icon={<CashSpecialIcon size={22} colors={[bodyCopy, bodyCopy]} />}
            className={`${selected === "CASH" ? "selected" : ""} funding-neutral-icon`.trim()}
            onChange={() => onPick({ kind: "method", category: "CASH" })}
          />
        )}
        {!listLoading && showOther && (
          <PayoutInfo
            title={t("payment.other")}
            subtitle=""
            icon={<WalletIcon size={22} colors={[bodyCopy, bodyCopy]} />}
            className={`${selected === "OTHER" ? "selected" : ""} funding-neutral-icon`.trim()}
            onChange={() => onPick({ kind: "method", category: "OTHER" })}
          />
        )}
        {!listLoading && showCards && (
          <PayoutInfo
            title={t("payment.addDebitCard")}
            subtitle={cardSubtitle}
            className="funding-brand-icon"
            icon={
              <AddCardIcon size={22} colors={[textColorBtn, textColorBtn]} />
            }
            onChange={() => onPick({ kind: "add" })}
          />
        )}
        {!listLoading && showBank && (
          <PayoutInfo
            title={t("payment.addBankAccount")}
            subtitle={bankSubtitle}
            className="funding-brand-icon"
            icon={<BankIcon size={22} colors={[textColorBtn, textColorBtn]} />}
            onChange={() => onPick({ kind: "addBank" })}
          />
        )}
        {!listLoading && !hasAny && (
          <PayoutInfo
            title={t("payment.none")}
            subtitle=""
            icon={<WalletIcon size={22} colors={[bodyCopy, bodyCopy]} />}
            locked
          />
        )}
      </section>
    </section>
  );
};

/** Map payment state → category highlight in the method list. */
export const categoryFromPayment = (
  type: paymentType | undefined
): PayoutMethodCategory | null => {
  if (type === paymentType.CASH) return "CASH";
  if (type === paymentType.CARD) return "CARD";
  if (type === paymentType.BANK) return "BANK";
  if (type === paymentType.OTHER) return "OTHER";
  return null;
};
